import React from 'react';
import { META_WHATSAPP_BASE, authHeaders, useSendMessageMutation } from 'api/metaWhatsappApi';
import useAppToast from 'components/toast';
import { explainRefusal } from '../_components/sendErrors';

/**
 * Sending a photo, document or voice note.
 *
 * ★ ONE ADAPTER. The file goes to the CRM's upload proxy
 * (`POST /conversations/:id/media`), which hands it to the connection layer
 * and answers with a media id; the message is then sent through the same
 * mutation as text, so the optimistic bubble, the idempotency key and the
 * refusal wording are all shared. When the transport changes, this is the
 * only file that changes.
 *
 * ★ NEVER A SENDER FIELD. The body is `{ type, media, context }` and nothing
 * that could choose a number.
 */
const UPLOAD_TEXT = {
	409: 'This conversation is not linked to a WhatsApp number, so attachments cannot be sent.',
	413: 'That file is over the size WhatsApp accepts.',
	415: 'WhatsApp does not accept that kind of file.',
	422: 'WhatsApp refused that file. Try a different format.',
	501: 'Attachments are not enabled on this workspace yet.',
	// ★ THE REMEDY IS RECONNECTING, NOT RETRYING. This used to say "try again in
	// a moment" for an expired WhatsApp token, which no amount of trying fixes
	// and which nobody would think to report to an administrator.
	503: 'WhatsApp needs reconnecting for this workspace. Ask an administrator.',
	// ★ THE FALL-THROUGH CASES, WHICH USED TO SAY NOTHING.
	// 502 is what the upload proxy returns for any refusal it cannot name —
	// a token missing its WhatsApp permissions, a file WhatsApp rejected, a
	// connection layer that does not have this route. None of them is the
	// agent's fault and none is fixed by pressing send again, so the sentence
	// has to point at an administrator. Without these entries the toast fell
	// all the way through to "The message could not be sent.", which is how a
	// misconfigured deployment looked exactly like a broken attachment.
	400: 'That file could not be read. Try a different file.',
	500: 'Something went wrong handling that file. Ask an administrator.',
	502: 'WhatsApp rejected this file. The workspace connection may need attention — ask an administrator.',
};

const newClientMessageId = () =>
	`cli_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export async function uploadMedia(conversationId, file, kind) {
	const form = new FormData();
	form.append('file', file, file.name);
	form.append('type', kind);
	const response = await fetch(
		`${META_WHATSAPP_BASE}/conversations/${encodeURIComponent(conversationId)}/media`,
		{ method: 'POST', headers: authHeaders(), body: form },
	);
	const body = await response.json().catch(() => ({}));
	if (!response.ok) {
		const err = new Error(body?.error || 'UPLOAD_FAILED');
		err.status = response.status;
		err.data = body;
		throw err;
	}
	return body;
}

export default function useOutboundMedia(conversation) {
	const [sendMessage] = useSendMessageMutation();
	const notify = useAppToast();
	const [isSending, setSending] = React.useState(false);

	const sendMedia = React.useCallback(
		async ({ file, kind, caption, replyTo }) => {
			if (!conversation?._id || !file) return false;
			setSending(true);
			let localUrl = null;
			try {
				const uploaded = await uploadMedia(conversation._id, file, kind);
				localUrl = URL.createObjectURL(file);
				await sendMessage({
					conversationId: conversation._id,
					clientMessageId: newClientMessageId(),
					type: kind,
					media: {
						id: uploaded.id,
						mimeType: uploaded.mimeType ?? file.type,
						filename: uploaded.filename ?? file.name,
						size: uploaded.size ?? file.size,
						...(caption ? { caption } : {}),
					},
					...(replyTo?.wamid ? { context: { messageId: replyTo.wamid } } : {}),
					localUrl,
				}).unwrap();
				return true;
			} catch (err) {
				const text = UPLOAD_TEXT[err?.status] ?? explainRefusal(err?.data);
				notify.error('Not sent', text);
				if (localUrl) URL.revokeObjectURL(localUrl);
				return false;
			} finally {
				setSending(false);
			}
		},
		[conversation?._id, sendMessage, notify],
	);

	return { sendMedia, isSending };
}
